/**
 * URL → locale（无依赖 i18n 实例，避免与 locales/index 循环引用）
 *
 * 支持参数（优先级）：lang > locale > language
 * 支持位置：?lang=zh#/...  与  #/path?lang=zh
 */

export const SUPPORTED_LOCALE_CODES = [
  'en',
  'zh',
  'zh-tw',
  'ja',
  'ko',
  'ms',
  'pt',
  'ru',
  'th',
  'vi',
  'ar',
  'es',
] as const;

export type SupportedLocaleCode = (typeof SUPPORTED_LOCALE_CODES)[number];

const LOCALE_QUERY_KEYS = ['lang', 'locale', 'language'] as const;

const localeAliasMap: Record<string, SupportedLocaleCode> = {
  'zh-cn': 'zh',
  'zh-hans': 'zh',
  cn: 'zh',
  'zh-hk': 'zh-tw',
  'zh-mo': 'zh-tw',
  'zh-hant': 'zh-tw',
  tw: 'zh-tw',
  jp: 'ja',
  kr: 'ko',
  eng: 'en',
  spa: 'es',
};

function getQueryParamFromUrl(keys: readonly string[]): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const search = new URLSearchParams(window.location.search || '');
    for (const key of keys) {
      const value = search.get(key);
      if (value && value.trim()) return value.trim();
    }
    const hash = window.location.hash || '';
    if (hash.includes('?')) {
      const hashParams = new URLSearchParams(hash.split('?')[1] || '');
      for (const key of keys) {
        const value = hashParams.get(key);
        if (value && value.trim()) return value.trim();
      }
    }
  } catch {
    // ignore
  }
  return null;
}

/** 归一化到项目支持的 locale；无法识别则返回 null */
export function normalizeLocale(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const normalized = String(raw).trim().toLowerCase().replace(/_/g, '-');
  if (!normalized) return null;
  if ((SUPPORTED_LOCALE_CODES as readonly string[]).includes(normalized)) {
    return normalized;
  }
  const aliased = localeAliasMap[normalized];
  if (aliased) return aliased;
  const base = normalized.split('-')[0];
  if ((SUPPORTED_LOCALE_CODES as readonly string[]).includes(base)) {
    return base;
  }
  return null;
}

/** 读取 URL 语言参数并归一化；无有效参数时返回 null */
export function getLocaleFromUrl(): string | null {
  return normalizeLocale(getQueryParamFromUrl(LOCALE_QUERY_KEYS));
}
